import {
  TherapistPaginationSearchResponseDto,
  TherapistSearchResponseDto,
} from '@app/api';
import { useIntersectionObserver } from '@app/hooks/useIntersectionObserver';
import { useCallback, useEffect, createRef, useState } from 'react';

interface Params {
  onIntersect?: (entry: IntersectionObserverEntry) => void;
  therapists?: TherapistPaginationSearchResponseDto;
  freeze?: boolean;
}

const defaultLoadedTherapists = { data: [], page: 1, total: 0 };

export const useInfiniteScroll = <T extends HTMLElement>({
  onIntersect,
  therapists,
  freeze,
}: Params) => {
  const loadMoreRef = createRef<T>();

  const [loadedTherapists, setLoadedTherapists] = useState<{
    data: TherapistSearchResponseDto[];
    page: number;
    total: number;
  }>(defaultLoadedTherapists);

  const entry = useIntersectionObserver(loadMoreRef, {
    freezeOnceVisible: freeze,
  });

  useEffect(() => {
    entry && onIntersect?.(entry);
  }, [entry?.isIntersecting]);

  useEffect(() => {
    if (therapists) {
      const hasEntireTherapists =
        therapists.total === loadedTherapists.data.length;

      if (hasEntireTherapists) return;

      setLoadedTherapists({
        data: [...loadedTherapists.data, ...therapists.data],
        page: therapists.page,
        total: therapists.total,
      });
    }
  }, [therapists]);

  const reset = useCallback(() => {
    setLoadedTherapists(defaultLoadedTherapists);
  }, []);

  return { loadMoreRef, loadedTherapists, reset };
};
